import React, { useEffect, lazy, Suspense } from "react";
import { MessageCircle } from "lucide-react";
import "./App.css";
import "./interactions.css";
import { MotionConfig } from 'framer-motion';
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SocketProvider } from "./contexts/SocketContext";
import { ToastProvider } from "./components/Toast";
import { ErrorBoundary } from "./components/ErrorBoundary";

/* Split per screen, because no single visitor needs both. ChatPage is by far the
   largest module in the app and a logged-out visitor cannot see any of it;
   AuthPage drags in GSAP, which nothing else uses. Bundling them together meant
   everybody paid for the half they were not looking at. */
const AuthPage = lazy(() => import("./pages/AuthPage"));
const ChatPage = lazy(() => import("./pages/ChatPage"));

const logBootTiming = (event) => {
  try {
    if (localStorage.getItem('whisper_debug_boot_timing') !== 'true') return;
    console.info('[whisper boot]', event, { t: Math.round(performance.now()) });
  } catch {
    // Debug logging should never affect startup.
  }
};

// Lightweight boot shell shown while auth is being checked.
const BootShell = () => (
  <div className="fixed inset-0 bg-surface-0 flex overflow-hidden">
    <div className="hidden md:flex md:flex-col w-[340px] border-r border-border-subtle bg-surface-1 p-4">
      <div className="flex items-center gap-3 mb-7 px-1">
        <div className="w-9 h-9 rounded-xl bg-brand-gradient-soft ui-skeleton" />
        <div className="flex-1">
          <div className="w-24 h-3.5 ui-skeleton" />
          <div className="w-16 h-2.5 ui-skeleton mt-2" />
        </div>
      </div>
      <div className="h-10 rounded-xl ui-skeleton mb-4" />
      <div className="space-y-1.5">
        {[0, 1, 2, 3, 4, 5].map((item) => (
          <div key={item} className="flex items-center gap-3 rounded-xl px-2 py-2.5">
            <div className="w-11 h-11 rounded-full ui-skeleton shrink-0" />
            <div className="flex-1">
              <div className="w-2/3 h-3 ui-skeleton" />
              <div className="w-1/2 h-2.5 ui-skeleton mt-2" />
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className="flex-1 flex flex-col bg-surface-0">
      <div className="h-16 border-b border-border-subtle bg-surface-1 flex items-center gap-3 px-4">
        <div className="w-10 h-10 rounded-full ui-skeleton" />
        <div>
          <div className="w-36 h-3.5 ui-skeleton" />
          <div className="w-20 h-2.5 ui-skeleton mt-2" />
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="neon-mark w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5">
            {/* White on the violet brand mark: 5.04:1 on its lightest stop. */}
            <div className="w-6 h-6 border-2 border-accent-foreground/70 border-t-transparent rounded-full animate-spin" />
          </div>
          <h1 className="text-white text-2xl font-semibold font-heading tracking-tight">Whisper</h1>
          <p className="text-zinc-500 mt-2 text-sm">Preparing your conversations…</p>
        </div>
      </div>
    </div>
  </div>
);

// Shown while the auth screen's chunk arrives. Deliberately NOT BootShell: that
// one is a mock of the chat UI, and flashing a fake conversation list at someone
// who is about to be asked to log in reads as a bug. This mirrors AuthPage's own
// frame instead, so the real form lands in the space the placeholder held.
const AuthShell = () => (
  <div className="h-dvh bg-surface-0 flex">
    <div className="flex-1 flex justify-center overflow-hidden p-6 md:p-8 pt-safe pb-safe">
      <div className="w-full max-w-[400px] my-auto">
        <div className="flex items-center gap-3 mb-9">
          <div className="neon-mark w-11 h-11 rounded-2xl flex items-center justify-center">
            <MessageCircle className="w-6 h-6" />
          </div>
          <h1 className="text-2xl font-bold text-white font-heading tracking-tight">Whisper</h1>
        </div>
        <div className="h-14 rounded-2xl ui-skeleton mb-7" />
        <div className="space-y-3">
          <div className="h-12 rounded-2xl ui-skeleton" />
          <div className="h-12 rounded-2xl ui-skeleton" />
          <div className="h-12 rounded-2xl ui-skeleton" />
        </div>
      </div>
    </div>
  </div>
);

// Main app content with auth check
const AppContent = () => {
  const { user, loading, authError, checkAuth } = useAuth();

  useEffect(() => {
    if (!loading) logBootTiming(user ? 'first-interactive-render' : 'auth-page-render');
  }, [loading, user]);

  /* Warm the chat chunk while the visitor is still reading the login form, so
     splitting the bundle does not turn "press Enter" into a visible wait. Idle
     time only — it must never compete with the form's own first paint. */
  useEffect(() => {
    if (loading || user) return undefined;
    const warm = () => { import("./pages/ChatPage"); };
    if (typeof requestIdleCallback === 'function') {
      const handle = requestIdleCallback(warm, { timeout: 2500 });
      return () => cancelIdleCallback(handle);
    }
    const timer = setTimeout(warm, 1200);
    return () => clearTimeout(timer);
  }, [loading, user]);

  if (loading) {
    return <BootShell />;
  }

  if (authError && !user) {
    return <div className="h-dvh bg-surface-0 flex items-center justify-center p-8"><div className="ui-card max-w-sm p-7 text-center"><MessageCircle className="h-9 w-9 text-accent mx-auto mb-4" /><h1 className="text-2xl mb-3">A moment to reconnect</h1><p className="text-sm text-zinc-400 leading-6 mb-6">We couldn’t check your session. Your conversations and sign-in are still safe. Try again when your connection is back.</p><button className="ui-btn ui-btn-primary" onClick={checkAuth}>Try again</button></div></div>;
  }

  if (!user) {
    return (
      <Suspense fallback={<AuthShell />}>
        <AuthPage />
      </Suspense>
    );
  }

  /* Suspense sits INSIDE SocketProvider on purpose: the socket handshake and the
     chat chunk download then happen at the same time instead of one after the
     other. */
  return (
    <SocketProvider>
      <Suspense fallback={<BootShell />}>
        <ChatPage />
      </Suspense>
    </SocketProvider>
  );
};

function App() {
  return (
    <div className="App">
      <MotionConfig reducedMotion="user">
      <ErrorBoundary>
        <ToastProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </ToastProvider>
      </ErrorBoundary>
      </MotionConfig>
    </div>
  );
}

export default App;
